import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "urql";

import { Asset } from "../../../components/graphql/schema.graphql";
import { useUrqlResponseHandler } from "../../../hooks";
import { UpdateAsset } from "../../asset/queries.graphql";

function getStem(filename: string) {
  const parts = filename.split(".");
  if (parts.length <= 1) return filename; // No extension found
  parts.pop(); // Remove the extension
  return parts.join(".");
}

type DriveRenameAssetDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  asset: Pick<Asset, "id" | "name">;
  refreshView: () => void;
};

export default function DriveRenameAssetDialog({
  isOpen,
  onClose,
  asset,
  refreshView,
}: DriveRenameAssetDialogProps) {
  const inputRef = useRef<HTMLInputElement>();
  const [newAssetName, setNewAssetName] = useState("");
  const [nameChanged, setNameChanged] = useState(false);
  const { getResponseFromState } = useUrqlResponseHandler();
  const [, updateAsset] = useMutation(UpdateAsset);

  useEffect(() => {
    setNewAssetName(asset?.name || "");
    setNameChanged(false);
  }, [asset?.name]);

  useEffect(() => {
    if (newAssetName && !nameChanged && inputRef.current) {
      inputRef.current.setSelectionRange(0, getStem(newAssetName).length);
    }
  }, [newAssetName]);

  const handleDialogClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    void updateAsset({
      updateAssetInput: {
        assetId: asset.id,
        name: newAssetName,
      },
    }).then((result) => {
      const { error } = getResponseFromState({
        state: result,
        errorMessage: "Failed to rename asset",
      });
      if (!error) {
        onClose();
        setNewAssetName("");
        refreshView();
      }
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "400px",
          },
        },
      }}
    >
      <DialogTitle>Rename File</DialogTitle>
      <DialogContent>
        <TextField
          inputRef={inputRef}
          autoFocus
          margin="dense"
          id="name"
          label="File Name"
          type="text"
          value={newAssetName}
          onChange={(event) => {
            setNewAssetName(event.target.value);
            setNameChanged(true);
          }}
          onKeyDown={(event) => {
            switch (event.key) {
              case "Enter":
                handleSubmit();
                break;
              case "Escape":
                handleDialogClose();
                break;
            }
          }}
          fullWidth
          variant="standard"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} variant="contained">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
}
